import type { StreamEvent } from '@dreamteam/chat-protocol';
import { ApiClient, type TokenSource } from './api.js';
import { CaptureRecorder, type CaptureOptions } from './capture/recorder.js';
import type { HostAdapter } from './host.js';
import { ChatWidget } from './ui/widget.js';

export type { Message, Ticket, StreamEvent, ScreenshotSelection, ElementDescriptor, CaptureEvent, FeatureRef } from '@dreamteam/chat-protocol';
export type { HostAdapter, HostFeature, HostChangelogEntry, HostChangelogItem } from './host.js';
export { collectSelection, describeElement, selectorFor } from './capture/dom.js';
export { CaptureRecorder } from './capture/recorder.js';

export const DEFAULT_FILE_EXTENSIONS = ['xlsx', 'xls', 'xlsm', 'csv', 'tsv', 'pdf', 'doc', 'docx', 'rtf', 'odt', 'ods', 'ppt', 'pptx', 'txt', 'md', 'json', 'xml', 'yaml', 'yml', 'log', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'heic', 'zip'];

export interface DreamTeamChatOptions {
  /** URL сервера DreamTeam Chat, например https://chat.dreamteam.ru или /dt (через прокси приложения). */
  endpoint: string;
  /** ID приложения из конфигурации сервера. */
  appId: string;
  /** JWT пользователя, выпущенный бэкендом приложения (строка или функция). */
  userToken?: TokenSource;
  /** Только для разработки (devAuth на сервере). */
  devUser?: { id: string; name?: string };
  locale?: 'ru' | 'en';
  /** Акцентный цвет под бренд приложения. */
  accent?: string;
  position?: 'right' | 'left';
  theme?: 'auto' | 'light' | 'dark';
  zIndex?: number;
  title?: string;
  /** false — без плавающей кнопки; открывайте через api.open() из своего меню. */
  launcher?: boolean;
  /** Для capture.mode = always: granted — техданные прикладываются по умолчанию; ask — пока пользователь не включит. */
  consent?: 'granted' | 'ask';
  /** Запись действий: manual (по умолчанию) — только пока пользователь нажал «Записать действия»; always — копится всегда. */
  capture?: CaptureOptions;
  screenshotMethod?: 'dom' | 'display';
  /** Разрешённые расширения файлов-вложений и предел размера (должны совпадать с сервером). */
  fileExtensions?: string[];
  maxFileBytes?: number;
  /** Что виджет берёт у приложения: функции за флагами, журнал изменений, переходы, состояние, тема. */
  host?: HostAdapter;
  onEvent?: (e: StreamEvent) => void;
  /** Куда монтировать (по умолчанию document.body). */
  mount?: HTMLElement;
}

export interface DreamTeamChatInstance {
  open(): void;
  close(): void;
  toggle(): void;
  /** Сразу перейти в режим скриншота (например, из пункта меню «Сообщить о проблеме»). */
  screenshot(): Promise<void>;
  /** Начать или остановить запись действий. */
  record(on?: boolean): void;
  showTickets(): void;
  showFeatures(): void;
  setUserToken(token: TokenSource): void;
  destroy(): void;
  readonly recorder: CaptureRecorder;
}

let sharedRecorder: CaptureRecorder | null = null;

/**
 * Установить перехват контекста как можно раньше (в <head>, до загрузки приложения).
 * В режиме manual (по умолчанию) перехватчики стоят, но ничего не копят, пока пользователь не нажмёт «Записать действия».
 */
export function installCapture(options: CaptureOptions = {}): CaptureRecorder {
  if (!sharedRecorder) {
    sharedRecorder = new CaptureRecorder({ mode: 'manual', ...options });
    sharedRecorder.install();
  }
  return sharedRecorder;
}

export function init(options: DreamTeamChatOptions): DreamTeamChatInstance {
  if (!options.endpoint || !options.appId) throw new Error('DreamTeamChat: endpoint и appId обязательны');
  const recorder = installCapture(options.capture);
  recorder.ignoreUrl(options.endpoint.replace(/\/$/, ''));
  const api = new ApiClient({ endpoint: options.endpoint, appId: options.appId, userToken: options.userToken, devUser: options.devUser });
  const widget = new ChatWidget(api, recorder, {
    locale: options.locale ?? 'ru',
    accent: options.accent ?? '#4f46e5',
    position: options.position ?? 'right',
    zIndex: options.zIndex ?? 2147483000,
    theme: options.theme ?? 'auto',
    launcher: options.launcher ?? true,
    consent: options.consent ?? 'granted',
    screenshotMethod: options.screenshotMethod ?? 'dom',
    title: options.title,
    fileExtensions: options.fileExtensions ?? DEFAULT_FILE_EXTENSIONS,
    maxFileBytes: options.maxFileBytes ?? 20 * 1024 * 1024,
    host: options.host ?? {},
    onEvent: options.onEvent,
  });
  const mountTo = options.mount ?? document.body;
  const start = () => {
    widget.mount(mountTo ?? document.body);
    void widget.start();
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });

  return {
    open: () => widget.open(),
    close: () => widget.close(),
    toggle: () => widget.toggle(),
    screenshot: async () => {
      widget.open();
      await widget.screenshot();
    },
    record: (on) => {
      if (on === undefined) widget.toggleRecording();
      else if (on) widget.startRecording();
      else widget.stopRecording();
    },
    showTickets: () => {
      widget.open();
      widget.setTab('tickets');
    },
    showFeatures: () => {
      widget.open();
      widget.setTab('features');
    },
    setUserToken: (t) => api.setToken(t),
    destroy: () => widget.destroy(),
    recorder,
  };
}
